import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";

import WhatsappTemplateDialog from "../../components/WhatsappTemplateDialog";
import WhatsappTemplatePreviewDialog from "../../components/WhatsappTemplatePreviewDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";
import TableManager from "../../components/TableManager/TableManager";
import AddIcon from "../../assets/icon/AddIcon";
import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import useWhatsappTemplates from "../../hooks/useWhatsappTemplates";
import { isWhatsappApiChannel } from "../../utils/connectionChannel";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { evaluateTemplateRowCategoryHints } from "../../utils/templateCategoryHints";
import { validateTemplateRowBeforeSubmit } from "../../utils/templateSubmitValidation";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  tableContainer: {
    width: "100%",
    maxWidth: "90%",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  filterField: {
    minWidth: 220,
  },
}));

const EDITABLE_STATUSES = ["DRAFT", "REJECTED"];
const SUBMITTABLE_STATUSES = ["DRAFT", "REJECTED"];

function statusColor(status) {
  switch (status) {
    case "APPROVED":
      return "primary";
    case "PENDING":
      return "default";
    case "REJECTED":
      return "secondary";
    case "DRAFT":
      return "default";
    default:
      return "default";
  }
}

function WhatsappTemplatesPage() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { list, create, update, remove, submit, sync, loading } = useWhatsappTemplates();

  const cloudLines = useMemo(
    () => (whatsApps ?? []).filter((line) => isWhatsappApiChannel(line.channel)),
    [whatsApps],
  );

  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [templateSelected, setTemplateSelected] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!selectedWhatsappId && cloudLines.length > 0) {
      setSelectedWhatsappId(String(cloudLines[0].id));
    }
  }, [cloudLines, selectedWhatsappId]);

  const selectedLine = useMemo(
    () => cloudLines.find((line) => String(line.id) === selectedWhatsappId),
    [cloudLines, selectedWhatsappId],
  );

  const loadTemplates = useCallback(async () => {
    if (!selectedWhatsappId) {
      setTemplates([]);
      return;
    }
    try {
      const result = await list({
        whatsappId: Number(selectedWhatsappId),
        limit: 100,
      });
      setTemplates(result.templates ?? []);
    } catch (err) {
      toastError(err);
    }
  }, [list, selectedWhatsappId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSync = async () => {
    if (!selectedWhatsappId) return;
    setSyncing(true);
    try {
      const result = await sync(Number(selectedWhatsappId));
      toast.success(
        i18n.t("whatsappTemplates.toasts.synced", { count: result?.synced ?? 0 }),
      );
      await loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        await update(id, {
          name: payload.name,
          language: payload.language,
          category: payload.category,
          components: payload.components,
        });
        toast.success(i18n.t("whatsappTemplates.toasts.updated"));
      } else {
        await create(payload);
        toast.success(i18n.t("whatsappTemplates.toasts.created"));
      }
      setModalOpen(false);
      setTemplateSelected(null);
      await loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (template) => {
    if (template.category !== "AUTHENTICATION") {
      const validation = validateTemplateRowBeforeSubmit(template);
      if (!validation.ok) {
        toast.error(
          `${i18n.t("whatsappTemplates.validation.submitBlocked")}\n• ${validation.messages.join("\n• ")}`,
        );
        return;
      }

      const categoryHints = evaluateTemplateRowCategoryHints(template);
      if (categoryHints.messages.length > 0) {
        toast.warn(i18n.t("whatsappTemplates.categoryHints.submitWarningShort"));
      }
    }

    try {
      await submit(template.id);
      toast.success(i18n.t("whatsappTemplates.toasts.submitted"));
      await loadTemplates();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!templateSelected?.id) return;
    try {
      await remove(templateSelected.id);
      toast.success(i18n.t("whatsappTemplates.toasts.deleted"));
      setDeleteOpen(false);
      setTemplateSelected(null);
      await loadTemplates();
    } catch (err) {
      toastError(err);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: i18n.t("whatsappTemplates.table.name"),
      headerStyle: { fontWeight: "400" },
      width: "20%",
    },
    {
      field: "language",
      headerName: i18n.t("whatsappTemplates.table.language"),
      headerStyle: { fontWeight: "400" },
      width: "10%",
    },
    {
      field: "category",
      headerName: i18n.t("whatsappTemplates.table.category"),
      headerStyle: { fontWeight: "400" },
      width: "15%",
      renderCell: ({ row }) =>
        i18n.t(`whatsappTemplates.categories.${row.category}`, row.category),
    },
    {
      field: "status",
      headerName: i18n.t("whatsappTemplates.table.status"),
      headerStyle: { fontWeight: "400" },
      width: "12%",
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={statusColor(row.status)}
          label={i18n.t(`whatsappTemplates.status.${row.status}`, row.status)}
        />
      ),
    },
    {
      field: "bodyPreview",
      headerName: i18n.t("whatsappTemplates.table.preview"),
      headerStyle: { fontWeight: "400" },
      width: "33%",
    },
  ];

  const actionButtons = [
    ({ row }) =>
      row.status === "DRAFT" ? null : (
        <IconButton
          size="small"
          title={i18n.t("whatsappTemplates.buttons.preview")}
          onClick={() => {
            setPreviewTemplate(row);
            setPreviewOpen(true);
          }}
        >
          <VisibilityOutlinedIcon style={{ color: "#7F7F7F", fontSize: 20 }} />
        </IconButton>
      ),
    ({ row }) =>
      EDITABLE_STATUSES.includes(row.status) ? (
        <IconButton
          size="small"
          onClick={() => {
            setTemplateSelected(row);
            setModalOpen(true);
          }}
        >
          <PencilEditIcon fill="#7F7F7F" />
        </IconButton>
      ) : null,
    ({ row }) =>
      SUBMITTABLE_STATUSES.includes(row.status) ? (
        <IconButton size="small" onClick={() => handleSubmit(row)} title={i18n.t("whatsappTemplates.buttons.submit")}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>↑</span>
        </IconButton>
      ) : null,
    ({ row }) => (
      <IconButton
        size="small"
        onClick={() => {
          setTemplateSelected(row);
          setDeleteOpen(true);
        }}
      >
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
    ),
  ];

  const primaryButtons = [
    {
      label: i18n.t("whatsappTemplates.buttons.add"),
      onClick: () => {
        setTemplateSelected(null);
        setModalOpen(true);
      },
      icon: <AddIcon fill="#000" />,
      disabled: cloudLines.length === 0,
    },
    {
      label: i18n.t("whatsappTemplates.buttons.sync"),
      onClick: handleSync,
      disabled: !selectedWhatsappId || syncing,
    },
  ];

  if (!user) return null;

  return (
    <div className={classes.mainContainer}>
      <div className={classes.tableContainer}>
        <HeaderManager title={i18n.t("whatsappTemplates.title")} primaryButtons={primaryButtons} />

        <div className={classes.filters}>
          <FormControl variant="outlined" className={classes.filterField} size="small">
            <InputLabel>{i18n.t("whatsappTemplates.dialog.line")}</InputLabel>
            <Select
              value={selectedWhatsappId}
              onChange={(e) => setSelectedWhatsappId(e.target.value)}
              label={i18n.t("whatsappTemplates.dialog.line")}
            >
              {cloudLines.map((line) => (
                <MenuItem key={line.id} value={String(line.id)}>
                  {line.name || `#${line.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {cloudLines.length === 0 ? (
          <p>{i18n.t("whatsappTemplates.emptyLines")}</p>
        ) : (
          <TableManager
            columns={columns}
            data={templates}
            loading={loading}
            actionButtons={actionButtons}
          />
        )}
      </div>

      <WhatsappTemplateDialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setTemplateSelected(null);
        }}
        templateSelected={templateSelected}
        cloudLines={cloudLines}
        onSave={handleSave}
        loadingSave={saving}
      />

      <WhatsappTemplatePreviewDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewTemplate(null);
        }}
        template={previewTemplate}
        businessName={selectedLine?.name}
      />

      <ConfirmationModal
        title={i18n.t("whatsappTemplates.delete.title")}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("whatsappTemplates.delete.body")}
      </ConfirmationModal>
    </div>
  );
}

export default WhatsappTemplatesPage;
